import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACSApi, ToastStateHandler } from '../../../api';
import { useContainer } from '../../useContainer';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { useTranslation } from '../../useTranslation';
import {
    setCachedProjects,
    setCachedArchivedProjects,
    setProjectsLoading,
    selectCachedProjects,
    selectCachedArchivedProjects,
    selectProjectsLastFetched,
    selectArchivedProjectsLastFetched,
} from '../../../store/slices/projects.slice';

const useHttpGetAllProjectsWithAssignees = (archived = false) => {
    const { t } = useTranslation('newNotifications');
    const acsApi: ACSApi = useContainer(ACSApi);
    const dispatch = useDispatch();
    const toastHandler: ToastStateHandler = useContainer(ToastStateHandler);

    const cachedProjects = useSelector(archived ? selectCachedArchivedProjects : selectCachedProjects);
    const lastFetched = useSelector(archived ? selectArchivedProjectsLastFetched : selectProjectsLastFetched);

    const [isReady, setIsReady] = useState(false);
    const [projects, setProjects] = useState<ProjectDtoRelV2[]>(cachedProjects);

    const fetchProjects = (forceRefresh = false) => {
        if (!forceRefresh && lastFetched !== null && cachedProjects.length > 0) {
            setProjects(cachedProjects);
            setIsReady(true);
            return;
        }

        if (!forceRefresh && lastFetched !== null && cachedProjects.length === 0) {
            setProjects([]);
            setIsReady(true);
            return;
        }

        dispatch(setProjectsLoading(true));

        const { exec, abort } = acsApi.getAllProjectsWithAssignees({
            query: archived ? { archived: true } : undefined,
            onSuccess: res => {
                dispatch(archived ? setCachedArchivedProjects(res.body) : setCachedProjects(res.body));
                setProjects(res.body);
            },
            onError: _ => toastHandler.push('error', t('common.error'), t('common.somethingWentWrong')),
            onFinally: () => setIsReady(true),
        });
        exec();
        return abort;
    };

    useEffect(() => {
        return fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- acsApi/dispatch/toastHandler/t are stable for the hook lifetime
    }, [lastFetched, cachedProjects, archived]);

    return { isReady, projects, setProjects, refetch: () => fetchProjects(true) };
};

export default useHttpGetAllProjectsWithAssignees;
